import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { API } from "@/context/AuthContext";
import { FORM, DASHBOARD } from "@/constants/testIds";

/**
 * Employee form.
 *
 * Sprint RBAC v2 additions (all strictly additive — nothing pre-existing
 * changes behaviour):
 *   • Multi-role assignment (checkbox list of `roles` fetched from /api/roles).
 *   • Per-user permission overrides editor (grant / revoke per module).
 *
 * The legacy `role` (free text) field is preserved for back-compat and
 * clearly labelled.
 */
export default function EmployeeForm({ initial, onSubmit, onCancel }) {
  const { t } = useI18n();
  const isEdit = !!initial?.id;

  // Basic fields
  const [name, setName] = useState(initial?.name || "");
  const [role, setRole] = useState(initial?.role || "");
  const [username, setUsername] = useState(initial?.username || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // RBAC v2 — role assignments + overrides
  const [allRoles, setAllRoles] = useState([]);
  const [catalog, setCatalog] = useState({ modules: [], actions: [] });
  const [roleIds, setRoleIds] = useState(initial?.role_ids || []);
  const [overrides, setOverrides] = useState(initial?.permission_overrides || {});
  const [showOverrides, setShowOverrides] = useState(false);

  useEffect(() => {
    setName(initial?.name || "");
    setRole(initial?.role || "");
    setUsername(initial?.username || "");
    setPassword("");
    setRoleIds(initial?.role_ids || []);
    setOverrides(initial?.permission_overrides || {});
    setShowOverrides(false);
  }, [initial]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [rr, cc] = await Promise.all([
          axios.get(`${API}/roles`, { withCredentials: true }),
          axios.get(`${API}/roles/permissions-catalog`, { withCredentials: true }),
        ]);
        if (!alive) return;
        setAllRoles(rr.data);
        setCatalog(cc.data);
      } catch {
        /* ignore — RBAC v2 is optional at form level */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const toggleRole = (id) => {
    setRoleIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const setOverride = (module, action, mode) => {
    // mode: null | "grant" | "revoke"
    setOverrides((prev) => {
      const next = { ...prev };
      const bucket = { ...(next[module] || {}) };
      const g = new Set(bucket.grant || []);
      const r = new Set(bucket.revoke || []);
      g.delete(action);
      r.delete(action);
      if (mode === "grant") g.add(action);
      if (mode === "revoke") r.add(action);
      const clean = {};
      if (g.size) clean.grant = Array.from(g);
      if (r.size) clean.revoke = Array.from(r);
      if (Object.keys(clean).length) next[module] = clean;
      else delete next[module];
      return next;
    });
  };

  const hasAnyOverride = useMemo(
    () => Object.keys(overrides || {}).length > 0,
    [overrides]
  );

  const handle = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // 1) Save the employee core (name/role/username/password + role_ids).
      const payload = { name, role, username, role_ids: roleIds };
      if (password) payload.password = password;
      await onSubmit(payload);

      // 2) If editing and overrides were touched, persist them via the
      //    dedicated endpoint. New employees don't have an id yet, so the
      //    parent (EmployeesPanel) reloads and users can set overrides on
      //    the next open.
      if (isEdit && initial?.id) {
        try {
          await axios.put(
            `${API}/users/${initial.id}/permission-overrides`,
            { overrides },
            { withCredentials: true }
          );
        } catch {
          toast.error("No se pudieron guardar los overrides");
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const input =
    "w-full border border-[var(--border)] bg-white px-3 py-2 focus:border-[var(--primary)]";

  return (
    <form onSubmit={handle} className="space-y-4">
      <div>
        <label className="label-overline block mb-1">{t("name")}</label>
        <input
          data-testid={FORM.empName}
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={input}
        />
      </div>

      <div>
        <label className="label-overline block mb-1">{t("role_legacy_field")}</label>
        <input
          data-testid={FORM.empRole}
          value={role}
          onChange={(e) => setRole(e.target.value)}
          className={input}
          placeholder="—"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label-overline block mb-1">{t("username")}</label>
          <input
            data-testid={FORM.empEmail}
            required
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className={input}
            autoComplete="off"
          />
        </div>
        <div>
          <label className="label-overline block mb-1">
            {t("password")}{" "}
            {isEdit && (
              <span className="text-[var(--text-secondary)] normal-case tracking-normal">
                ({t("leave_blank_to_keep")})
              </span>
            )}
          </label>
          <input
            data-testid="form-employee-password"
            type="password"
            required={!isEdit}
            minLength={isEdit ? undefined : 4}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={input}
            autoComplete="new-password"
          />
        </div>
      </div>

      {/* RBAC v2 — Multi-role assignment */}
      {allRoles.length > 0 && (
        <div>
          <label className="label-overline block mb-1">{t("employee_roles_label")}</label>
          <p className="text-xs text-[var(--text-secondary)] mb-2">{t("employee_roles_hint")}</p>
          <div className="flex flex-wrap gap-2 border border-[var(--border)] bg-white p-3">
            {allRoles.map((r) => {
              const active = roleIds.includes(r.id);
              return (
                <button
                  type="button"
                  key={r.id}
                  data-testid={DASHBOARD.employeeRolesChip(r.id)}
                  onClick={() => toggleRole(r.id)}
                  className={`text-xs font-mono-tech tracking-wide px-3 py-1.5 border transition-colors ${
                    active
                      ? "bg-black text-white border-black"
                      : "bg-white text-[var(--text-primary)] border-[var(--border)] hover:bg-[var(--bg)]"
                  }`}
                >
                  {r.name}
                  {r.is_admin ? " ★" : ""}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* RBAC v2 — Per-user permission overrides (edit mode only) */}
      {isEdit && catalog.modules.length > 0 && (
        <div>
          <button
            type="button"
            data-testid={DASHBOARD.employeeOverridesToggle}
            onClick={() => setShowOverrides((v) => !v)}
            className="w-full text-left border border-[var(--border)] bg-[var(--bg)] px-3 py-2 font-mono-tech text-xs tracking-wider hover:bg-white"
          >
            {showOverrides ? "▼" : "▶"} {t("employee_overrides_title")}
            {hasAnyOverride && (
              <span className="ml-2 inline-block px-1.5 py-0.5 bg-black text-white text-[0.6rem]">
                ● {Object.keys(overrides).length}
              </span>
            )}
          </button>
          {showOverrides && (
            <div className="border border-t-0 border-[var(--border)] bg-white p-3 space-y-2 max-h-[45vh] overflow-auto">
              <p className="text-xs text-[var(--text-secondary)]">
                {t("employee_overrides_hint")}
              </p>
              <table className="w-full text-xs border-collapse">
                <thead className="sticky top-0 bg-[var(--bg)]">
                  <tr>
                    <th className="text-left px-2 py-1.5 font-mono-tech tracking-wider">MOD</th>
                    {catalog.actions.map((a) => (
                      <th key={a} className="px-1 py-1.5 font-mono-tech tracking-wider text-center">
                        {t(`perm_act_${a}`) || a}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {catalog.modules.map((m) => (
                    <tr key={m} className="border-t border-[var(--border)]">
                      <td className="px-2 py-1.5 font-display font-semibold whitespace-nowrap">
                        {t(`perm_mod_${m}`) || m}
                      </td>
                      {catalog.actions.map((a) => {
                        const bucket = overrides?.[m] || {};
                        const isGrant = (bucket.grant || []).includes(a);
                        const isRevoke = (bucket.revoke || []).includes(a);
                        const state = isGrant ? "grant" : isRevoke ? "revoke" : "none";
                        return (
                          <td key={a} className="px-1 py-1.5 text-center">
                            <div className="inline-flex border border-[var(--border)]">
                              <button
                                type="button"
                                data-testid={DASHBOARD.employeeOverrideGrant(m, a)}
                                onClick={() =>
                                  setOverride(m, a, state === "grant" ? null : "grant")
                                }
                                className={`w-6 h-6 text-[0.6rem] font-bold ${
                                  isGrant
                                    ? "bg-green-600 text-white"
                                    : "bg-white text-[var(--text-secondary)] hover:bg-green-50"
                                }`}
                                title={t("employee_override_grant")}
                              >
                                +
                              </button>
                              <button
                                type="button"
                                data-testid={DASHBOARD.employeeOverrideRevoke(m, a)}
                                onClick={() =>
                                  setOverride(m, a, state === "revoke" ? null : "revoke")
                                }
                                className={`w-6 h-6 text-[0.6rem] font-bold border-l border-[var(--border)] ${
                                  isRevoke
                                    ? "bg-[var(--alert-overdue)] text-white"
                                    : "bg-white text-[var(--text-secondary)] hover:bg-red-50"
                                }`}
                                title={t("employee_override_revoke")}
                              >
                                −
                              </button>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="flex gap-3 pt-2">
        <button
          data-testid={FORM.empCancel}
          type="button"
          onClick={onCancel}
          className="flex-1 border border-[var(--border)] px-4 py-2.5 font-display font-medium hover:bg-[var(--surface-alt)]"
        >
          {t("cancel")}
        </button>
        <button
          data-testid={FORM.empSubmit}
          type="submit"
          disabled={loading}
          className="flex-1 bg-[var(--text-primary)] text-white px-4 py-2.5 font-display font-bold hover:bg-[var(--primary)] disabled:opacity-50"
        >
          {loading ? "..." : t("save")}
        </button>
      </div>
    </form>
  );
}
